import { createContext, useContext, type ReactNode } from 'react';
import { useMe, type MeState } from './use-me.js';

/**
 * One GET /api/me for the whole app, shared by everything that needs it.
 *
 * Three things now read it — the header (for a name), the gate (to decide
 * whether the profile exists) and the account panel — and they all render at
 * once. Calling useMe in each would be three requests per page load, and worse,
 * three copies of the answer that can disagree while they settle.
 *
 * It also makes the reload after profile completion work. The gate renders the
 * form INSTEAD of the route, so navigating away does not remount anything;
 * something has to tell this to fetch again, and that is `reload`.
 */

interface MeContextValue {
  state: MeState;
  reload: () => void;
}

const MeContext = createContext<MeContextValue | null>(null);

export function MeProvider({ children }: { children: ReactNode }) {
  const value = useMe();
  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

/**
 * Throws outside the provider rather than returning a default.
 *
 * A default would mean a component silently behaving as though nobody is signed
 * in, which for the gate means showing the profile form to someone who already
 * has one. Failing loudly at development time is cheaper than that.
 */
export function useMeContext(): MeContextValue {
  const value = useContext(MeContext);
  if (!value) throw new Error('useMeContext used outside MeProvider.');
  return value;
}
